import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/core';
import { StarIcon } from '@chakra-ui/icons';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCandidate from '../../app/components/useCandidate';
import useLookupsByType from '../../app/hooks/useLookupsByType';
import { AppointmentType } from '../../firebase/firestore/documents/position';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { getCandidateRatingAndReview, isCandidateRated } from '../../utils/candidatesUtils';
import { getTranslatedValue } from '../../utils/localizedString';
import { CandidateInfoForGuest } from '../meet/MeetTokenResponse';

export type GuestInterviewRatingProps = {
  candidateInfo: CandidateInfoForGuest;
  showReviewRating: boolean;
  setShowReviewRating: (isOpen: boolean) => void;
  quitRoom: () => void;
  canRateCandidate?: boolean;
  candidateId: string;
};

export const GuestInterviewRating = ({
  candidateInfo,
  showReviewRating,
  setShowReviewRating,
  quitRoom,
  canRateCandidate,
  candidateId,
}: GuestInterviewRatingProps): JSX.Element => {
  const [rating, setRating] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const interviewDate = new Date();
  const interviewType = AppointmentType.video;
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const loggedInUserName = useStoreState((s) => s.app.user?.firstName);
  const loggedInUserEmail = useStoreState((s) => s.app.user?.email);
  const ratingStars: number[] = Array.from({ length: 5 }, (_, i) => i);
  const lookups = useLookupsByType('negativeAssessmentReasons');
  const { t, i18n } = useTranslation('messaging');
  const toast = useToast();
  const isDisabled = (): boolean => {
    if (!remarks.trim()) return true;
    if (rating > 1) return false;
    if (rating <= 1 && reason) return false;
    return true;
  };
  const signOut = useStoreActions((actions) => actions.auth.signOut);
  const { collaboratorRateInterview } = useStoreActions((actions) => actions.meet);
  const guestEmail = useStoreState((state) => state.meet.guestEmail);
  const setHasRated = useStoreActions((actions) => actions.meet.setHasRated);
  const { candidate, loading: isLoadingCandidate } = useCandidate(candidateId);
  const userEmail = loggedInUserEmail || /* istanbul ignore next */ guestEmail;
  const isRated = candidate ? isCandidateRated(candidate, userEmail) : /* istanbul ignore next */ false;
  const ratingData = candidate ? getCandidateRatingAndReview(candidate, userEmail) : /* istanbul ignore next */ null;
  // istanbul ignore next
  useEffect(() => {
    if (ratingData) {
      setRating(ratingData.rating);
      setRemarks(ratingData.review);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingData]);

  const handleSubmit = async (): Promise<void | null> => {
    const splitCandidateId = candidateId.split('_');
    const seekerId = splitCandidateId[1];
    const positionId = splitCandidateId[0];
    try {
      // istanbul ignore else
      if (!_isNil(interviewDate) && !_isNil(loggedInUserName)) {
        setSubmitting(true);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        await collaboratorRateInterview({
          positionId,
          seekerId,
          rating,
          assessmentReason: reason,
          date: interviewDate,
          interviewType,
          businessName: candidateInfo?.businessLocation,
          collaborator: loggedInUserName,
          review: remarks,
          email: loggedInUserEmail /* istanbul ignore next */ ?? guestEmail,
        });
      }
      if (showReviewRating) {
        quitRoom();
        // eslint-disable-next-line
        setHasRated(isRated);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        signOut();
      } else {
        setRating(0);
        setReason('');
        setRemarks('');
        toast({
          title: t('interviewRating.successful'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        setHasRated(isRated);
      }
    } catch (e) {
      toast({
        title: t('interviewRating.unsuccessful'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    setSubmitting(false);
    onClose();
  };

  useEffect(() => {
    if (showReviewRating && !isRated) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReviewRating, isRated]);
  // istanbul ignore next
  useEffect(() => {
    if (isRated) {
      setShowReviewRating(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRated, setShowReviewRating]);

  return (
    <>
      <Button
        data-testid="candidateAssessmentBtn"
        colorScheme="blue"
        size="sm"
        onClick={onOpen}
        mb={canRateCandidate ? /* istanbul ignore next */ 2 : undefined}
        mr={canRateCandidate ? /* istanbul ignore next */ 2 : undefined}
      >
        {canRateCandidate ? /* istanbul ignore next */ t('interviewRating.rateCandidate') : t('interviewRating.title')}
      </Button>
      <Modal
        closeOnOverlayClick={false}
        onClose={() => {
          onClose();
          setShowReviewRating(false);
        }}
        isOpen={isOpen}
      >
        <ModalOverlay />
        <ModalContent borderRadius="20px" maxWidth="55rem">
          <ModalCloseButton data-testid="closeBtn" />
          <ModalBody style={{ marginTop: '20px', padding: '25px' }} data-testid="InterviewRatingModal">
            <Box mb={6}>
              <Heading as="h5" size="md">
                {t('interviewRating.title')}
              </Heading>
            </Box>

            {isLoadingCandidate ? (
              // istanbul ignore next
              <Flex justify="center" align="center" minHeight="250px">
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
              </Flex>
            ) : (
              <Box>
                <FormControl isRequired>
                  <FormLabel>{t('interviewRating.feedbackDetail')}</FormLabel>
                </FormControl>
                <Box mt={5} mb={6}>
                  {ratingStars.map((k) => {
                    const val: number = k + 1;
                    let color = '#FF6600';
                    if (rating < val) {
                      color = '#FFFFFF';
                    }

                    return (
                      <StarIcon
                        boxSize={7}
                        color={color}
                        style={{ marginRight: '10px', cursor: 'pointer', stroke: '#FF6600' }}
                        onClick={() => {
                          setRating(val);
                          setReason('');
                        }}
                        key={k}
                        data-testid="RatingStars"
                      />
                    );
                  })}
                </Box>
                {rating && rating <= 1 ? (
                  <RadioGroup
                    onChange={(rsn) => {
                      setReason(rsn as string);
                    }}
                    name="lowReason"
                    value={reason}
                    mt={5}
                    mb={5}
                    data-testid="RatingReasonsGroup"
                  >
                    <FormControl isRequired>
                      <FormLabel fontWeight="bold">{t('interviewRating.lowReason')}</FormLabel>
                      <Stack>
                        {lookups.map((lu) => {
                          const label = getTranslatedValue(lu.label, i18n.language);
                          return (
                            <Radio value={lu.id} key={label} data-testid="RatingReasons" paddingBottom="5px">
                              <Text fontSize="sm">{label}</Text>
                            </Radio>
                          );
                        })}
                      </Stack>
                    </FormControl>
                  </RadioGroup>
                ) : null}
                <Box>
                  <FormControl isRequired>
                    <FormLabel fontWeight="bold">{t('interviewRating.recruitersFeedback')}</FormLabel>
                    <Textarea
                      placeholder={t('interviewRating.commentPlaceholder')}
                      border="none"
                      value={remarks}
                      mt={1}
                      borderRadius="3px"
                      p={4}
                      bg="gray.150"
                      data-testid="RatingRemarksInput"
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <Button
                    colorScheme="blue"
                    style={{ float: 'right', marginTop: '20px' }}
                    isDisabled={isDisabled()}
                    isLoading={submitting}
                    onClick={() => handleSubmit()}
                    name="Submit"
                    data-testid="SubmitRatingButton"
                  >
                    {t('actions.hire.confirmationModal.sendButton')}
                  </Button>
                </Box>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
